import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useInHouse } from "../../context/inHousePermissions";
import { allowedRoutesFor } from "../../utils/roleAccess";
import client from "../../api/client";
import User from "../User";
import "./Sidebar.css";

const navItems = [
  { path: "/", label: "Главная" },
  { path: "/pulse", label: "Пульс" },
  { path: "/sales", label: "Продажи" },
  { path: "/refunds", label: "Возвраты" },
  { path: "/transactions", label: "Транзакции" },
  { path: "/editor", label: "Редактура" },
  { path: "/log", label: "Логистика" },
  { path: "/storage", label: "Хранилище" },
  { path: "/promo-codes", label: "Промокоды" },
  { path: "/team", label: "Команда" },
];

interface SearchHit {
  id: number;
  username: string;
}

export const Sidebar: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { currentUserRecord } = useInHouse();
  const navigate = useNavigate();

  const allowedRoutes = allowedRoutesFor(
    currentUserRecord?.role,
    currentUserRecord?.extraRoutes
  );

  const visibleItems = navItems.filter((item) =>
    allowedRoutes.includes(item.path)
  );

  // === User search ===
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loadingHit, setLoadingHit] = useState<number | null>(null);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setHits([]);
      return;
    }
    // 250 мс дебаунс — реагируем на «прекратил печатать», не тратим
    // запрос на каждый ввод.
    const t = window.setTimeout(async () => {
      try {
        const { data } = await client.get<SearchHit[]>("/admin/users/search", {
          params: { q, limit: 5 },
        });
        setHits(Array.isArray(data) ? data : []);
      } catch {
        setHits([]);
      }
    }, 250);
    return () => window.clearTimeout(t);
  }, [query]);

  // Клик за пределами поиска закрывает дропдаун.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openUser = async (hit: SearchHit) => {
    if (loadingHit) return;
    setLoadingHit(hit.id);
    try {
      const { data } = await client.get(`/orders/${hit.username}`);
      // /orders/:idOrUsername может вернуть order без username — добиваем
      // его руками, иначе попап покажет пустое имя.
      setSelectedUser({ username: hit.username, ...data });
      setDropdownOpen(false);
      setQuery("");
      setHits([]);
    } catch (err) {
      console.error("Failed to load order for", hit.username, err);
    } finally {
      setLoadingHit(null);
    }
  };

  return (
    <aside className="sidebar">
      <div className="sidebar__logo" onClick={() => navigate("/")}>
        Comabooks
      </div>

      <div className="sidebar__search" ref={searchRef}>
        <input
          className="sidebar__search-input"
          type="text"
          placeholder="Поиск юзера…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setDropdownOpen(true);
          }}
          onFocus={() => setDropdownOpen(true)}
        />
        {dropdownOpen && query.trim() && hits.length > 0 && (
          <div className="sidebar__search-dropdown">
            {hits.map((hit) => (
              <div
                key={hit.id}
                className="sidebar__search-item"
                onClick={() => openUser(hit)}
                style={{ opacity: loadingHit === hit.id ? 0.5 : 1 }}
              >
                {hit.username}
              </div>
            ))}
          </div>
        )}
        {dropdownOpen && query.trim() && hits.length === 0 && (
          <div className="sidebar__search-dropdown sidebar__search-dropdown--empty">
            ничего не найдено
          </div>
        )}
      </div>

      <nav className="sidebar__nav">
        {visibleItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === "/"}
            className={({ isActive }) =>
              `sidebar__link ${isActive ? "sidebar__link--active" : ""}`
            }
          >
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="sidebar__footer">
        <div className="sidebar__user">
          <div className="sidebar__user-name">
            {currentUserRecord?.name || currentUser?.username}
          </div>
          <div className="sidebar__user-role">{currentUserRecord?.role}</div>
        </div>
        <button className="sidebar__logout" onClick={logout}>
          Выйти
        </button>
      </div>

      {/* Порталом в body: .sidebar — position: fixed, это свой контекст
          наложения, и z-index попапа внутри него проигрывает элементам
          страницы (липкие шапки таблиц хранилища и т.п.). */}
      {selectedUser &&
        createPortal(
          <User
            user={selectedUser}
            onClose={() => setSelectedUser(null)}
            onUpdate={() => {}}
            deadline="—"
          />,
          document.body
        )}
    </aside>
  );
};
